import { Box, Flex, Menu, MenuButton, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { FiChevronLeft, FiMoreHorizontal } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';

import { ActionMenuList } from 'components/ActionMenuList';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaPlusIcon from 'components/Icons/FigmaPlusIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import DeleteModal from 'components/Modals/DeleteModal';
import FigmaFileDownloadIcon from 'components/Icons/FigmaFileDownloadIcon';
import {
  PLAN_TEXT,
  PLAN_TABLE_CELL_BORDER,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import {
  deleteVersionIFile,
  editVersionFile,
  getSelectedVersionDetails,
  getVersionList,
  setSelectedVersionDetails,
  versionData,
} from 'redux/DataImportSlice';
import { fontSize } from 'styles/revampTokens';
import { getCsvExportBlockReason, getOriginalDownloadExtension } from 'utils/dataSourceDownload';

import colors from './dataTheme';
import { CountPill, SectionTitle, controlStyle, iconButtonStyle } from './detailUi';
import EditSourceModal from './EditSourceModal';
import { formatCreatedOn } from './sourcesTableUtils';
import useVersionDownload from './useVersionDownload';

interface Properties {
  dataSourceId: number;
  sourceName: string;
  onAddVersion: () => void;
}

const VersionsPanel = ({ dataSourceId, sourceName, onAddVersion }: Properties) => {
  const dispatch: any = useDispatch();
  const versionList = useSelector(getVersionList);
  const selectedVersion = useSelector(getSelectedVersionDetails);
  const versions: any[] = versionList?.versions ?? [];
  const { download, downloadingId } = useVersionDownload(dataSourceId, sourceName);

  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [menuView, setMenuView] = useState<'main' | 'download'>('main');
  const [renameTarget, setRenameTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const refetchKeepingSelection = async () => {
    const selectedId = selectedVersion?.id;
    const result: any = await dispatch(versionData(dataSourceId));
    const kept = result?.payload?.versions?.find((version: any) => version.id === selectedId);
    if (kept) dispatch(setSelectedVersionDetails(kept));
  };

  const saveName = async (name: string) => {
    if (!renameTarget) return;
    await dispatch(
      editVersionFile({
        dataSourceId,
        versionId: renameTarget.id,
        request_body: { name },
        toaster: {
          successMessage: 'Successfully updated',
          errorMessage: 'A version with this name already exists',
        },
      }),
    );
    setRenameTarget(null);
    await refetchKeepingSelection();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await dispatch(deleteVersionIFile({ dataSourceId, versionId: deleteTarget.id }));
    setDeleteTarget(null);
    await dispatch(versionData(dataSourceId));
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuView('main');
  };

  const renderMenu = (version: any) => {
    const csvBlockReason = getCsvExportBlockReason(version);
    const isDownloading = downloadingId === version.id;
    const mainItems = [
      {
        key: 'rename',
        label: 'Rename',
        icon: <FigmaEditIcon width={14} height={14} color={colors.textDark} />,
        onSelect: () => {
          closeMenu();
          setRenameTarget(version);
        },
      },
      {
        key: 'download',
        label: 'Download',
        icon: <FigmaFileDownloadIcon width={14} height={14} color={colors.textDark} />,
        onSelect: () => setMenuView('download'),
      },
      ...(versions.length > 1
        ? [
            {
              key: 'delete',
              label: 'Delete',
              icon: <FigmaTrashIcon width={14} height={14} color={colors.danger} />,
              color: colors.danger,
              onSelect: () => {
                closeMenu();
                setDeleteTarget(version);
              },
            },
          ]
        : []),
    ];
    const downloadItems = [
      {
        key: 'back',
        label: 'Back',
        icon: <FiChevronLeft size={14} color={colors.textDark} />,
        onSelect: () => setMenuView('main'),
      },
      {
        key: 'original',
        label: `Download original (.${getOriginalDownloadExtension(version.storage_format)})`,
        icon: <FigmaFileDownloadIcon width={14} height={14} color={colors.textDark} />,
        isDisabled: isDownloading,
        onSelect: () => {
          closeMenu();
          download(version, 'original');
        },
      },
      {
        key: 'csv',
        label: 'Download as CSV',
        icon: <FigmaFileDownloadIcon width={14} height={14} color={colors.textDark} />,
        isDisabled: isDownloading || Boolean(csvBlockReason),
        tooltip: csvBlockReason ?? undefined,
        onSelect: () => {
          closeMenu();
          download(version, 'csv');
        },
      },
    ];

    return (
      <Menu
        placement='bottom-end'
        strategy='fixed'
        closeOnSelect={false}
        isOpen={openMenuId === version.id}
        onOpen={() => setOpenMenuId(version.id)}
        onClose={closeMenu}
      >
        <MenuButton
          {...iconButtonStyle}
          aria-label={`Actions for ${version.name}`}
          onClick={(event) => event.stopPropagation()}
        >
          <FiMoreHorizontal size={15} />
        </MenuButton>
        <ActionMenuList minW='210px' items={menuView === 'download' ? downloadItems : mainItems} />
      </Menu>
    );
  };

  return (
    <Flex direction='column' h='100%' minH={0}>
      <Flex alignItems='center' gap={2} mb={2} flexShrink={0}>
        <SectionTitle>Versions</SectionTitle>
        <CountPill>{versions.length}</CountPill>
      </Flex>

      <Flex direction='column' gap='2px' flex={1} minH={0} overflowY='auto' className='scroll'>
        {versions.map((version) => {
          const isActive = selectedVersion?.id === version.id;
          return (
            <Flex
              key={version.id}
              role='group'
              alignItems='center'
              gap={3}
              px={2}
              py='8px'
              borderRadius='8px'
              cursor='pointer'
              bg={isActive ? colors.activeBg : 'transparent'}
              transition='background 0.12s ease'
              _hover={{ bg: isActive ? colors.activeBg : colors.hover }}
              onClick={() => dispatch(setSelectedVersionDetails(version))}
            >
              <Flex
                w='14px'
                h='14px'
                flexShrink={0}
                alignItems='center'
                justifyContent='center'
                borderRadius='full'
                border='1.5px solid'
                borderColor={isActive ? colors.primary : colors.border}
              >
                {isActive && <Box w='6px' h='6px' borderRadius='full' bg={colors.primary} />}
              </Flex>
              <Box flex={1} minW={0}>
                <Flex alignItems='center' gap={2}>
                  <Text
                    fontSize={fontSize.body}
                    fontWeight={isActive ? 600 : 500}
                    color={isActive ? colors.primary : colors.textDark}
                    noOfLines={1}
                    title={version.name}
                  >
                    {version.name}
                  </Text>
                  {isActive && (
                    <Text
                      {...PLAN_TEXT.badge}
                      px='6px'
                      borderRadius='4px'
                      bg={colors.surface}
                      color={colors.primary}
                      flexShrink={0}
                    >
                      Active
                    </Text>
                  )}
                </Flex>
                <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} noOfLines={1}>
                  {formatCreatedOn(version.date_created)}
                </Text>
              </Box>
              <Box flexShrink={0}>{renderMenu(version)}</Box>
            </Flex>
          );
        })}
      </Flex>

      <Flex
        as='button'
        type='button'
        {...controlStyle}
        w='100%'
        justifyContent='center'
        mt={3}
        flexShrink={0}
        borderStyle='dashed'
        borderColor={PLAN_TABLE_CELL_BORDER}
        onClick={onAddVersion}
      >
        <FigmaPlusIcon width={12} height={12} color={colors.textDark} />
        Add Version
      </Flex>

      <EditSourceModal
        isOpen={Boolean(renameTarget)}
        initialName={renameTarget?.name ?? ''}
        title='Edit version'
        fieldLabel='Version name'
        onClose={() => setRenameTarget(null)}
        onSave={saveName}
      />
      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        itemName={deleteTarget?.name ?? ''}
        onDelete={confirmDelete}
      />
    </Flex>
  );
};

export default VersionsPanel;
